import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type RecordingMetadata, useRecordingSession } from 'providers/RecordingSessionProvider';
import { type ReconnectEpisode, type WebSocketErrorState } from 'hooks/useRecordingWebSocket';
import { type ManualNotes } from 'hooks/useManualNotes';
import type { useChunkQueue } from 'hooks/useChunkQueue';

export const RECONNECT_BUDGET_MS = 60_000;
const RECONNECT_BACKOFF_MS = [0, 500, 1000, 2000, 4000];
const RECONNECT_MAX_BACKOFF_MS = 8_000;
const MIN_ATTEMPT_MS = 1_000;

async function withTimeout<T>(budgetMs: number, run: () => Promise<T>): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const expiry = new Promise<never>((_resolve, reject) => {
    const timedOut = Object.assign(new Error('Reconnect attempt timed out'), {
      code: 'connection_closed',
    });
    timer = setTimeout(() => reject(timedOut), budgetMs);
  });

  try {
    return await Promise.race([run(), expiry]);
  } finally {
    clearTimeout(timer);
  }
}

// A shutting-down backend reports upstream_unavailable; a reconnect reaches a healthy pod.
const RETRYABLE_SERVER_CODES = new Set(['upstream_unavailable']);

const RECONNECT_RAPID_WINDOW_MS = 10_000;
const RECONNECT_MAX_RAPID_EPISODES = 3;

export function readErrorCode(err: unknown): { code?: string; correlationId?: string } {
  return typeof err === 'object' && err !== null ? err : {};
}

export interface ReconnectSession {
  metadata: RecordingMetadata;
  getNotes: () => ManualNotes;
  isFailed: () => boolean;
  failRecording: (error: WebSocketErrorState) => void;
}

interface ErrorClassification {
  fatal: WebSocketErrorState | null;
  retryable: WebSocketErrorState | null;
}

export function useRecordingReconnect(chunkQueue: ReturnType<typeof useChunkQueue>) {
  const {
    connect: wsConnect,
    start: wsStart,
    sendNotes: wsSendNotes,
    disconnect: wsDisconnect,
    consumeServerError,
  } = useRecordingSession();
  const [episode, setEpisode] = useState<ReconnectEpisode | null>(null);
  const sessionRef = useRef<ReconnectSession | null>(null);
  const headerChunkRef = useRef<Blob | null>(null);
  const reconnectingRef = useRef<Promise<void> | null>(null);
  const generationRef = useRef(0);
  const rapidEpisodesRef = useRef(0);
  const lastEpisodeStartRef = useRef(0);

  const endSession = useCallback(() => {
    generationRef.current += 1;
    sessionRef.current = null;
    headerChunkRef.current = null;
    reconnectingRef.current = null;
    rapidEpisodesRef.current = 0;
    lastEpisodeStartRef.current = 0;
  }, []);

  const beginSession = useCallback(
    (session: ReconnectSession) => {
      endSession();
      sessionRef.current = session;
    },
    [endSession]
  );

  const setSessionMetadata = useCallback((metadata: RecordingMetadata) => {
    if (sessionRef.current) sessionRef.current.metadata = metadata;
  }, []);

  const getInteractionId = useCallback(() => sessionRef.current?.metadata.interactionId, []);

  const captureHeaderChunk = useCallback((chunk: Blob) => {
    if (headerChunkRef.current === null) headerChunkRef.current = chunk;
  }, []);

  const pending = useCallback(() => reconnectingRef.current, []);

  const handleSendError = useCallback(
    (err: unknown) => {
      const session = sessionRef.current;
      if (session === null) return;
      const generation = generationRef.current;
      const sessionEnded = () => session.isFailed() || generationRef.current !== generation;

      const classifyError = (candidate: unknown): ErrorClassification => {
        const serverError = consumeServerError();
        const { code, correlationId } = serverError ?? readErrorCode(candidate);
        if (!code || code === 'connection_closed') return { fatal: null, retryable: null };
        const errorState = { code, correlationId };
        if (RETRYABLE_SERVER_CODES.has(code)) return { fatal: null, retryable: errorState };
        return { fatal: errorState, retryable: null };
      };

      const attemptReconnect = async (
        initialError: WebSocketErrorState
      ): Promise<WebSocketErrorState | null> => {
        let lastError = initialError;
        chunkQueue.pause();
        const deadline = Date.now() + RECONNECT_BUDGET_MS;
        for (let attempt = 0; ; attempt += 1) {
          const remainingMs = deadline - Date.now();
          if (remainingMs <= 0) break;
          const backoffMs = RECONNECT_BACKOFF_MS[attempt] ?? RECONNECT_MAX_BACKOFF_MS;
          const delayMs = Math.min(backoffMs, remainingMs);
          if (delayMs > 0) await new Promise((resolve) => setTimeout(resolve, delayMs));
          if (sessionEnded()) return null;
          const attemptBudgetMs = Math.max(deadline - Date.now(), MIN_ATTEMPT_MS);
          try {
            const started = await withTimeout(attemptBudgetMs, async () => {
              await wsConnect();
              return wsStart({
                ...session.metadata,
                notes: session.getNotes(),
                sameRecorder: true,
              });
            });
            if (sessionEnded()) {
              wsDisconnect();
              return null;
            }
            if (started.resumed) wsSendNotes(session.getNotes());
            // A fresh stream needs the WebM header; a reattached one must not see a second header.
            if (!started.reattached) {
              chunkQueue.requeueSentTail(headerChunkRef.current);
            }
            await chunkQueue.setReady();
            return null;
          } catch (attemptErr) {
            wsDisconnect();
            const { fatal, retryable } = classifyError(attemptErr);
            if (fatal) return fatal;
            if (retryable) lastError = retryable;
          }
        }
        return lastError;
      };

      const { fatal, retryable } = classifyError(err);
      if (fatal) {
        session.failRecording(fatal);
        return;
      }
      if (reconnectingRef.current !== null) return;
      const initialError = retryable ?? {
        code: 'connection_closed',
        correlationId: readErrorCode(err).correlationId,
      };
      const now = Date.now();
      rapidEpisodesRef.current =
        now - lastEpisodeStartRef.current < RECONNECT_RAPID_WINDOW_MS
          ? rapidEpisodesRef.current + 1
          : 0;
      lastEpisodeStartRef.current = now;
      if (rapidEpisodesRef.current >= RECONNECT_MAX_RAPID_EPISODES) {
        session.failRecording(initialError);
        return;
      }
      setEpisode({ startedAt: now, reasonCode: initialError.code });
      reconnectingRef.current = attemptReconnect(initialError)
        .then((failure) => {
          if (failure && !sessionEnded()) session.failRecording(failure);
        })
        .finally(() => {
          reconnectingRef.current = null;
          setEpisode(null);
        });
    },
    [chunkQueue, consumeServerError, wsConnect, wsDisconnect, wsSendNotes, wsStart]
  );

  useEffect(() => endSession, [endSession]);

  return useMemo(
    () => ({
      episode,
      beginSession,
      endSession,
      setSessionMetadata,
      getInteractionId,
      captureHeaderChunk,
      handleSendError,
      pending,
    }),
    [
      episode,
      beginSession,
      endSession,
      setSessionMetadata,
      getInteractionId,
      captureHeaderChunk,
      handleSendError,
      pending,
    ]
  );
}
